import React, { useState } from 'react';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import Predictions from './tabs/Predictions';
import FairAssessment from './tabs/FairAssessment';
import Analytics from './tabs/Analytics';
import Crashes from './tabs/Crashes';
import Basics from './tabs/Basics';
import Network from './tabs/Network';
import Violations from './tabs/Violations';
import RemovedViolations from './tabs/RemovedViolations';
import Inspections from './tabs/Inspections';
import Fleet from './tabs/Fleet';
import Overview from './tabs/Overview';
import StatusHistoryTimeline from './StatusHistoryTimeline';
import AuthorityHistoryTimeline from './AuthorityHistoryTimeline';
import InsuranceHistoryTable from './InsuranceHistoryTable';
import TabNavigation, { type CarrierTabId } from './tabs/TabNavigation';
import { useViolationsSummary } from './hooks/useViolationsSummary';
import type { CarrierTabsProps } from './CarrierTabs.types';
export type {
  BASICScore,
  ObservedUnitsData,
  HistoricStatsData,
  VINMatch,
  ContactMatches,
  ContactMatchData,
} from './CarrierTabs.types';

export const CarrierTabs: React.FC<CarrierTabsProps> = ({
  dotNumber,
  profile,
  inspections,
  violations,
  crashes,
  vehicles,
  basics,
  censusData,
  observedUnits,
  historicStats,
  totalInspections,
  totalViolations,
  totalCrashes,
  loadingMore,
  onLoadMoreInspections,
  onLoadMoreViolations,
  onLoadMoreCrashes,
  contactMatches,
  className = '',
  loadingDetails = false,
  setSelectedVin = () => {},
  setSelectedInspection = () => {},
  setTimelineVin = () => {},
  // Shell game props with defaults
  shellGameAlerts = [],
  checkingShellGame = false,
  hasCheckedShellGame = false,
  loadCarrier = () => {},
  checkShellGame = () => {},
}) => {
  // Tab state — 'authority-insurance' is registered locally here (the shared
  // TabNavigation component owns the other tab ids).
  const [activeTab, setActiveTab] = useState<CarrierTabId | 'authority-insurance'>('overview');
  const [expandedViolationSeverity, setExpandedViolationSeverity] = useState<string | null>(null);

  // BASIC scores loading state (basics are preloaded as prop)
  const basicsLoading = false;

  // Violations summary + per-severity grouping (extracted hook)
  const { violationsSummary, violationsBySeverity } = useViolationsSummary(dotNumber, violations);

  return (
    <>
          <TabNavigation
            activeTab={activeTab as CarrierTabId}
            setActiveTab={setActiveTab}
            basicsCount={basics.length}
            violationsCount={violationsSummary ? violationsSummary.total : violations.length}
            inspectionsCount={inspections.length}
            crashesCount={crashes.length}
            vehiclesCount={vehicles.length}
          >
            {/* AUTHORITY & INSURANCE HISTORY tab button — styled to match
                TabNavigation's buttons and rendered as its child so it wraps
                and aligns in the same row instead of sitting in a separate
                box below. Registered here (not in TabNavigation's CarrierTabId
                union) because that shared type is outside this change's scope. */}
            <button
              type="button"
              onClick={() => setActiveTab('authority-insurance')}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                background: activeTab === 'authority-insurance' ? 'var(--color-primary)' : 'white',
                color: activeTab === 'authority-insurance' ? 'white' : 'var(--color-gray-700)',
                border: `1px solid ${activeTab === 'authority-insurance' ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
                borderRadius: 'var(--radius-md)',
                fontWeight: '500',
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-xs)'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'authority-insurance') {
                  e.currentTarget.style.background = 'var(--color-gray-50)';
                  e.currentTarget.style.borderColor = 'var(--color-gray-400)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'authority-insurance') {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                }
              }}
            >
              <span>Authority &amp; Insurance</span>
            </button>
          </TabNavigation>

          {/* Loading Details */}
          {loadingDetails && (
            <div style={{ textAlign: 'center', padding: 'var(--space-3xl) 0' }}>
              <LoadingSpinner message="Loading detailed data..." size="lg" />
            </div>
          )}

          {/* Tab Content */}
          {!loadingDetails && (
            <>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <Overview
                  profile={profile}
                  violations={violations}
                  inspections={inspections}
                  crashes={crashes}
                  basics={basics}
                  vehicles={vehicles}
                  violationsSummary={violationsSummary}
                  violationsBySeverity={violationsBySeverity}
                  expandedViolationSeverity={expandedViolationSeverity}
                  setExpandedViolationSeverity={setExpandedViolationSeverity}
                  setSelectedVin={setSelectedVin}
                  setSelectedInspection={setSelectedInspection}
                />
              )}

              {/* FEATURE 2: ANALYTICS TAB - COMPREHENSIVE DASHBOARD */}
              {activeTab === 'analytics' && (
                <Analytics
                  profile={profile}
                  violations={violations}
                  crashes={crashes}
                  inspections={inspections}
                  basics={basics}
                  vehicles={vehicles}
                  observedUnits={observedUnits}
                  dotNumber={dotNumber}
                />
              )}

              {/* BASIC SCORES TAB */}
              {activeTab === 'basics' && (
                <Basics
                  profile={profile}
                  basics={basics}
                  basicsLoading={basicsLoading}
                />
              )}

              {/* BASIC PREDICTIONS TAB */}
              {activeTab === 'predictions' && dotNumber && (
                <Predictions dotNumber={dotNumber} />
              )}

              {/* FAIR ASSESSMENT TAB */}
              {activeTab === 'fair-assessment' && dotNumber && (
                <FairAssessment dotNumber={dotNumber} basics={basics} />
              )}

              {/* VIOLATIONS TAB */}
              {activeTab === 'violations' && (
                <Violations
                  profile={profile}
                  violations={violations}
                  inspections={inspections}
                  violationsSummary={violationsSummary}
                  violationsBySeverity={violationsBySeverity}
                  loadingMore={loadingMore}
                  onLoadMoreViolations={onLoadMoreViolations}
                  setSelectedVin={setSelectedVin}
                  setSelectedInspection={setSelectedInspection}
                />
              )}

              {/* REMOVED VIOLATIONS TAB */}
              {activeTab === 'removed-violations' && dotNumber && (
                <RemovedViolations dotNumber={Number(dotNumber)} />
              )}

              {/* INSPECTIONS TAB */}
              {activeTab === 'inspections' && (
                <Inspections
                  dotNumber={dotNumber}
                  profile={profile}
                  inspections={inspections}
                  totalInspections={totalInspections}
                  loadingMore={loadingMore}
                  onLoadMoreInspections={onLoadMoreInspections}
                  setSelectedInspection={setSelectedInspection}
                  setTimelineVin={setTimelineVin}
                />
              )}

              {/* CRASHES TAB */}
              {activeTab === 'crashes' && (
                <Crashes
                  profile={profile}
                  crashes={crashes}
                  loadingMore={loadingMore}
                  onLoadMoreCrashes={onLoadMoreCrashes}
                  setSelectedVin={setSelectedVin}
                />
              )}

              {/* VIN INTELLIGENCE TAB */}
              {activeTab === 'vins' && (
                <Fleet
                  vehicles={vehicles}
                  observedUnits={observedUnits}
                  setSelectedVin={setSelectedVin}
                  setTimelineVin={setTimelineVin}
                  shellGameAlerts={shellGameAlerts}
                  checkingShellGame={checkingShellGame}
                  hasCheckedShellGame={hasCheckedShellGame}
                  loadCarrier={loadCarrier}
                  checkShellGame={checkShellGame}
                />
              )}

              {/* STATUS HISTORY TAB */}
              {activeTab === 'status-history' && dotNumber && (
                <StatusHistoryTimeline dotNumber={dotNumber} />
              )}

              {/* AUTHORITY & INSURANCE HISTORY TAB */}
              {activeTab === 'authority-insurance' && dotNumber && (
                <div className="space-y-6">
                  <AuthorityHistoryTimeline dotNumber={dotNumber} />
                  <InsuranceHistoryTable dotNumber={dotNumber} />
                </div>
              )}

              {/* FRAUD NETWORK TAB */}
              {activeTab === 'network' && dotNumber && (
                <Network
                  dotNumber={dotNumber}
                  contactMatches={contactMatches}
                  shellGameAlerts={shellGameAlerts}
                  setSelectedVin={setSelectedVin}
                />
              )}

            </>
          )}
    </>
  );
};
